import { router, useNavigation } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { KeyboardAvoidingView, Platform } from 'react-native';
import type { Edge } from 'react-native-safe-area-context';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    CheckIcon,
    Icon,
    isWeb,
    SafeAreaView,
    ScrollView,
    useToastController,
    View,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import {
    emptyBillNew,
    getBillTitle,
    getTimingValuesForBill,
    transformBillFromServer,
} from '../../../common/utils/billUtils';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../logger/Logger';
import useCreateBill, { type CreateBillBody } from '../../hooks/useCreateBill';
import {
    activeBillsAtom,
    addBillCategoryAtom,
    allBillsAtom,
    billsToReviewAtom,
    currentlyEditingBillFieldAtom,
    manualBillAtom,
    selectedReviewBillsAtom,
} from '../../state/BillsAtoms';
import {
    type _Bill,
    BillContext,
    type IBill,
    InlineEditableBillFields,
    type PartialBill,
    type SelectableBill,
} from '../../types/Bill';
import BillsHeader from '../common/BillsHeader';
import ManualBillAttributes from './ManualBillAttributes';

interface ManualBillProps {
    context?: BillContext;
}

const ManualBill = ({ context }: ManualBillProps) => {
    LogRocketFacade.tagPage('ManualBill');

    const navigation = useNavigation();
    const user = useAuthedUser();
    const toast = useToastController();
    const { mutate: createBill } = useCreateBill();

    const [bill, setBill] = useAtom(manualBillAtom);
    const setCurrentlyEditingBillField = useSetAtom(
        currentlyEditingBillFieldAtom,
    );
    const storeActiveBills = useSetAtom(activeBillsAtom);
    const storeAllBills = useSetAtom(allBillsAtom);
    const storeBillsToReview = useSetAtom(billsToReviewAtom);
    const storeSelectedReviewBills = useSetAtom(selectedReviewBillsAtom);
    const setManualBill = useSetAtom(manualBillAtom);
    const addBillCategory = useAtomValue(addBillCategoryAtom);

    const [isBillValid, setIsBillValid] = useState(false);

    useEffect(() => {
        setIsBillValid(
            Object.keys(bill)
                .filter((key) => key !== 'displayName')
                .every((key) => bill[key as keyof IBill] !== undefined) &&
                (bill.amount ?? 0) > 0,
        );
    }, [bill]);

    // if someone comes here via the missing bills block, automatically set the category based on what they selected
    useEffect(() => {
        if (addBillCategory !== null) {
            setBill({ ...bill, category: addBillCategory });
        }
    }, []);

    // sets the currently editing field back to amount for the next time the modal is opened
    navigation.addListener('beforeRemove', (e) => {
        if (['GO_BACK', 'POP'].includes(e.data.action.type)) {
            setCurrentlyEditingBillField(InlineEditableBillFields.AMOUNT);
            if (context === BillContext.REVIEW) setManualBill(emptyBillNew());
        }
    });

    const storeBill = (transformedBill: _Bill) => {
        storeActiveBills((prev) => {
            if (!prev.length) return [transformedBill];
            return prev.concat(transformedBill);
        });
        storeAllBills((prev) => {
            if (!prev.length) return [transformedBill];
            return prev.concat(transformedBill);
        });
        if (context === BillContext.REVIEW) {
            storeBillsToReview((prev) => {
                if (!prev.length) return [transformedBill];
                return prev.concat(transformedBill);
            });
            storeSelectedReviewBills((prev) => {
                if (!prev.length)
                    return [
                        {
                            ...transformedBill,
                            isSelected: true,
                        } satisfies SelectableBill,
                    ];

                return prev.concat({
                    ...transformedBill,
                    isSelected: true,
                } satisfies SelectableBill);
            });
        }
    };

    const onSubmit = () => {
        const {
            amount,
            payee,
            displayName: name,
            accountId,
            cadence,
            category,
            lastPaidAt,
        } = bill as IBill;

        createBill(
            {
                urlParams: {
                    membershipId: user.membership?.id ?? '',
                },
                body: {
                    accountId,
                    name,
                    payee,
                    amount,
                    cadence,
                    category,
                    lastPaidAt,
                    ...getTimingValuesForBill(
                        bill,
                        bill.lastPaidAt ?? new Date(),
                    ),
                } satisfies CreateBillBody,
            },
            {
                onSuccess: (newBillFromServer) => {
                    const transformedBill =
                        transformBillFromServer(newBillFromServer);
                    storeBill(transformedBill);

                    toast.show(`Added!`, {
                        description: `New ${getBillTitle(transformedBill)} bill`,
                        icon: (
                            <View
                                alignItems="center"
                                justifyContent="center"
                                backgroundColor="$green1000"
                                borderRadius={12}
                                padding={9}
                            >
                                <Icon
                                    icon={CheckIcon}
                                    size={24}
                                    color="$white"
                                />
                            </View>
                        ),
                    });
                    router.back();
                },
                onError: (error) => {
                    toast.show('Something went wrong');
                    captureException(error);
                    // TODO handle error UX
                },
            },
        );
    };

    return (
        <>
            <StatusBar
                style={context === BillContext.REVIEW ? 'dark' : 'light'}
            />
            <SafeAreaView
                edges={[
                    'right',
                    'bottom',
                    'left',
                    ...(context === BillContext.REVIEW ? ['top' as Edge] : []),
                ]}
            >
                <KeyboardAvoidingView
                    style={{
                        flex: 1,
                        height: '100%',
                        ...(isWeb && {
                            justifyContent: 'center',
                            alignItems: 'center',
                        }),
                    }}
                    behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                    keyboardVerticalOffset={
                        context === BillContext.REVIEW ? 20 : 90
                    }
                >
                    <YStack
                        flex={1}
                        padding={20}
                        paddingTop={
                            isWeb ? 24 : context === BillContext.REVIEW ? 0 : 20
                        }
                        paddingBottom={0}
                        justifyContent="space-between"
                        gap={16}
                        maxWidth={700}
                        width="100%"
                    >
                        <BillsHeader
                            title="New bill"
                            onBack={() => {
                                router.back();
                            }}
                            padding={0}
                        />
                        <ScrollView showsVerticalScrollIndicator={isWeb}>
                            <ManualBillAttributes
                                bill={bill}
                                onUpdateBill={(bill: PartialBill) => {
                                    setBill({
                                        ...bill,
                                        lastPaidAt:
                                            bill.lastPaidAt ?? undefined,
                                    });
                                }}
                                context={context}
                            />
                        </ScrollView>
                    </YStack>
                    <View
                        width="100%"
                        borderTopWidth={1}
                        borderColor="$borderColor"
                        maxWidth={700}
                    >
                        <View
                            paddingTop={20}
                            paddingBottom={isWeb ? 20 : undefined}
                            paddingHorizontal={20}
                        >
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.LARGE}
                                text="Add"
                                onPress={onSubmit}
                                disabled={!isBillValid}
                            />
                        </View>
                    </View>
                </KeyboardAvoidingView>
            </SafeAreaView>
        </>
    );
};

export default ManualBill;
